import {useCallback, useEffect, useMemo, useState} from 'react';

import {searchApprovalRequests} from 'src/openapi/admin/apis/RequestSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import type {ApprovalRequestSearchCriteria} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';

type Props = {
    initialCriteria?: Partial<ApprovalRequestSearchCriteria>,
    autoLoad?: boolean,
};

const SEARCH_ERROR_TIMEOUT = 5000;

const defaultSearchCriteria: ApprovalRequestSearchCriteria = {
    page: 1,
    pageSize: 25,
};

export const useApprovalRequestSearchCriteria = ({initialCriteria}: Props = {}) => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();

    const [searchCriteria, setSearchCriteria] = useState<ApprovalRequestSearchCriteria>({
        ...defaultSearchCriteria,
        ...initialCriteria,
    });

    const hasCriteriaChanges = useCallback((
        prev: ApprovalRequestSearchCriteria,
        criteria: Partial<ApprovalRequestSearchCriteria>,
    ) => {
        return (Object.keys(criteria) as Array<keyof ApprovalRequestSearchCriteria>).some(key => {
            const prevValue = prev[key];
            const nextValue = criteria[key];

            if (Array.isArray(prevValue) && Array.isArray(nextValue)) {
                return prevValue.length !== nextValue.length || prevValue.some(
                    (value, index) => value !== nextValue[index],
                );
            }

            return prevValue !== nextValue;
        });
    }, []);

    const loadData = useCallback(async () => {
        const result = await apiDispatch(searchApprovalRequests({
            approvalRequestSearchCriteria: searchCriteria,
        }));
        const payload = (result as {
            payload?: {
                error?: boolean,
                success?: boolean,
                data?: {detailMessages?: string[]},
            },
        }).payload;

        if (payload?.error) {
            const detailMessage = payload.data?.detailMessages?.[0];
            const message = detailMessage
                ? `${detailMessage}.`
                : 'Unable to load requests. Please refine your search.';
            onCreateNote({
                id: `request-search-error-${Date.now()}`,
                message,
                status: Status.ERROR,
                autoHideTimeout: SEARCH_ERROR_TIMEOUT,
            });
        }
    }, [apiDispatch, onCreateNote, searchCriteria]);

    const updateSearchCriteria = useCallback((criteria: Partial<ApprovalRequestSearchCriteria>) => {
        setSearchCriteria(prev => {
            if (!hasCriteriaChanges(prev, criteria)) {
                return prev;
            }

            return {...prev, ...criteria};
        });
    }, [hasCriteriaChanges]);

    const memoizedSearchCriteria = useMemo(() => searchCriteria, [searchCriteria]);

    useEffect(() => {
        if (searchCriteria.requestStatuses !== undefined) {
            loadData();
        }
    }, [loadData, searchCriteria.requestStatuses]);

    return {
        loadData,
        searchCriteria: memoizedSearchCriteria,
        updateSearchCriteria,
    };
};
